import { Router } from '../core/router/router.js';
import { createElement, clearElement } from '../core/utils/dom.js';
import { toolCard } from '../ui/tool-card.js';
import { breadcrumbs } from '../ui/breadcrumbs.js';
import { getRelatedTools } from '../core/discovery/related.js';

export class App {
  constructor() {
    this.router = new Router();
    this.container = null;
    this.categoryRegistry = null;
    this.toolRegistry = null;
    this.searchService = null;
    this._ready = null;
  }

  async ensureLoaded() {
    if (!this._ready) {
      this._ready = (async () => {
        const { loadRegistries } = await import('./bootstrap.js');
        const registries = await loadRegistries();
        this.categoryRegistry = registries.categoryRegistry;
        this.toolRegistry = registries.toolRegistry;
        this.searchService = registries.searchService;
        return registries;
      })();
    }
    return this._ready;
  }

  async init() {
    this.container = document.getElementById('page-container');

    this.router.register('/', async () => {
      await this.ensureLoaded();
      const { render } = await import('./pages/home.js');
      render(this.container, {
        categoryRegistry: this.categoryRegistry,
        toolRegistry: this.toolRegistry
      });
    });
    this.router.register('/categories/', async () => {
      await this.ensureLoaded();
      const { render } = await import('./pages/categories.js');
      render(this.container, this.categoryRegistry, this.toolRegistry);
    });
    this.router.register('/categories/:slug/', async ({ params }) => {
      await this.ensureLoaded();
      const { render } = await import('./pages/category-detail.js');
      render(this.container, this.categoryRegistry, this.toolRegistry, params.slug);
    });
    this.router.register('/tools/:slug/', ({ params }) => this.loadTool(params.slug));
    this.router.register('/search/', async ({ query }) => {
      const q = query.get('q') || '';
      await this.ensureLoaded();
      const { render } = await import('./pages/search.js');
      render(this.container, this.searchService, q, this.categoryRegistry);
    });

    this.router.setNotFound(async () => {
      const { render } = await import('./pages/not-found.js');
      render(this.container);
    });

    return this.router.start();
  }

  async loadTool(slug) {
    await this.ensureLoaded();
    const tool = this.toolRegistry.getBySlug(slug);

    if (!tool) {
      const { render } = await import('./pages/not-found.js');
      render(this.container);
      return;
    }

    try {
      const category = this.categoryRegistry.getById(tool.category);

      const crumbs = breadcrumbs([
        { label: 'الرئيسية', href: '#/' },
        ...(category
          ? [{ label: 'الفئات', href: '#/categories/' }, { label: category.name, href: `#/categories/${category.slug}/` }]
          : []),
        { label: tool.name }
      ]);

      const mount = document.createElement('div');
      mount.className = 'tool-mount';

      const renderFn = await tool.implementation();

      clearElement(this.container);
      this.container.appendChild(crumbs);
      this.container.appendChild(mount);
      renderFn(mount);

      const related = getRelatedTools(tool, this.toolRegistry.getAll(), 4);
      if (related.length > 0 || category) {
        const section = createElement('section', { className: 'related-tools', attributes: { 'aria-label': 'أدوات ذات صلة' } });
        section.appendChild(createElement('h2', { className: 'section-title', text: 'أدوات ذات صلة' }));
        const grid = createElement('div', { className: 'tool-grid' });
        for (const relatedTool of related) {
          grid.appendChild(toolCard(relatedTool));
        }
        section.appendChild(grid);
        if (category) {
          section.appendChild(createElement('a', {
            className: 'btn btn-secondary',
            text: `المزيد من أدوات ${category.name}`,
            attributes: { href: `#/categories/${category.slug}/` }
          }));
        }
        this.container.appendChild(section);
      }
    } catch (error) {
      clearElement(this.container);
      const errorDiv = document.createElement('div');
      errorDiv.className = 'alert alert-error';
      errorDiv.textContent = 'حدث خطأ أثناء تحميل الأداة. يرجى المحاولة مرة أخرى.';
      this.container.appendChild(errorDiv);
    }
  }
}
